import React from 'react';
import { motion } from 'framer-motion';

const Hero: React.FC = () => {
  return (
    <section id="hero" className="min-h-screen flex items-center relative overflow-hidden pt-20">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-radial from-electric-blue/20 to-transparent opacity-30"></div>
      
      <div className="container mx-auto px-4 z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="gradient-text">Empowering Businesses</span> with AI-Driven Agents
            </h1>
            <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto lg:mx-0">
              We develop intelligent AI products and agents to automate your business workflows, 
              increasing efficiency and reducing operational costs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <motion.a 
                href="#contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-electric-blue to-teal-accent rounded-md text-deep-blue font-bold text-lg shadow-neon hover:shadow-neon-lg transition-all"
              >
                Unlock Intelligent Automation
              </motion.a>
              <motion.a 
                href="#solutions"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-transparent border-2 border-electric-blue rounded-md text-white font-bold text-lg hover:bg-electric-blue/10 transition-all"
              >
                Explore Solutions
              </motion.a>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative w-full h-[400px] lg:h-[500px]">
              {/* Abstract AI visualization */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-64 h-64 rounded-full bg-gradient-to-r from-electric-blue/30 to-teal-accent/30 animate-pulse-slow"></div>
                <div className="absolute w-48 h-48 rounded-full border-4 border-electric-blue/50 animate-spin" style={{ animationDuration: '15s' }}></div>
                <div className="absolute w-32 h-32 rounded-full border-2 border-teal-accent/70 animate-spin" style={{ animationDuration: '10s', animationDirection: 'reverse' }}></div>
                <div className="absolute w-16 h-16 rounded-full bg-electric-blue/80 animate-pulse"></div>
                
                {/* Connection lines */}
                <div className="absolute w-full h-full">
                  {[...Array(8)].map((_, i) => (
                    <div 
                      key={i} 
                      className="absolute w-1 bg-gradient-to-b from-electric-blue/80 to-transparent"
                      style={{ 
                        height: '120px', 
                        left: '50%', 
                        top: '50%',
                        transform: `rotate(${i * 45}deg) translateY(-100px)`,
                        transformOrigin: 'bottom center'
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
      >
        <span className="text-sm text-gray-400 mb-2">Scroll to explore</span>
        <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
          <motion.div 
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-2 h-2 bg-electric-blue rounded-full mt-2"
          ></motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;